import YouTubeIframeLoader from 'youtube-iframe';
import Vimeo from '@vimeo/player';

class Video {
  static init(...parameters) {
    return new Video(...parameters);
  }

  constructor(container) {
    this.container = container;
    if (container) {
      this.content = container.querySelector('.content');
      this.video = container.querySelector('.video');
      this.paused = true;

      if (this.video) {
        this.videoType = this.video.dataset.videoType;
        this.videoStart = this.video.dataset.videoStart;
        this.videoControls = this.video.dataset.videoControls;
        this.loopVideo = this.video.dataset.videoLoop === '1';
        this.startVideoWhenInView = this.video.dataset.videoStartInView === '1';
        this.inViewPercent = Number(this.video.dataset.videoInViewPercent);

        if (this.videoType === 'youtube') {
          this.initYotube();
        }

        if (this.videoType === 'vimeo') {
          this.initVimeo();
        }

        if (this.startVideoWhenInView) {
          const observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                // in view
                if (entry.intersectionRatio > 0) {
                  if (this.videoType === 'youtube') {
                    this.youtube.mute();
                    this.youtube.playVideo();
                  }

                  if (this.videoType === 'vimeo') {
                    this.vimeo.play();
                  }

                  observer.unobserve(entry.target);
                }
              });
            },
            {
              threshold: [this.inViewPercent], // [0, 0.25, 0.5, 1],
              rootMargin: '0px 0px -15% 0px',
            },
          );
          observer.observe(this.container);
        }
      }
    }
  }

  initYotube() {
    const { video } = this;

    const player = video.querySelector('.player');
    const { videoId } = video.dataset;
    if (player && videoId) {
      YouTubeIframeLoader.load((YT) => {
        this.youtube = new YT.Player(player, {
          height: '100%',
          width: '100%',
          videoId,
          events: {
            onReady: () => {
              this.iframe = video.querySelector('iframe');
              if (!this.videoControls && !this.startVideoWhenInView) {
                this.youtube.mute();
                this.youtube.playVideo();
                player.classList.remove('-hidden');
              }
            },
          },
          playerVars: {
            start: this.videoStart,
            controls: this.videoControls ? 1 : 0, // Show pause/play buttons in player
            showinfo: 0, // Hide the video title
            modestbranding: 1, // Hide the Youtube Logo
            fs: 0, // Hide the full screen button
            cc_load_policy: 0, // Hide closed captions
            iv_load_policy: 3, // Hide the Video Annotations
            autohide: 0,
            rel: 0, // Hide video controls when playing
            disablekb: 1, // disable the keyboard controls
            // eslint-disable-next-line no-nested-ternary
            loop: this.startVideoWhenInView ? this.loopVideo : this.videoControls ? 0 : 1,
            playlist: videoId,
          },
        });
      });
    }
  }

  initVimeo() {
    const player = this.video.querySelector('.player');
    const { videoId } = this.video.dataset;

    const options = {
      url: `https://vimeo.com/${videoId}`,
      background: !this.videoControls && !this.startVideoWhenInView,
      responsive: true,
    };

    if (this.startVideoWhenInView) {
      options.autoplay = false;
      options.autopause = false;
      options.loop = this.loopVideo;
      options.muted = true;
      options.controls = this.videoControls ? 1 : 0;
    }

    if (player) {
      let isReady = false;
      const vimeo = new Vimeo(player, options);
      vimeo.ready().then(() => {
        vimeo.setCurrentTime(this.videoStart);
      });
      vimeo.on('play', () => {
        if (!isReady) {
          isReady = true;
          this.onReady();
        }
        vimeo.on('timeupdate', (data) => {
          if (data.percent > 0) {
            vimeo.off('timeupdate');
            this.paused = false;
          }
        });
      });
      vimeo.on('pause', () => {
        vimeo.off('timeupdate');
        this.paused = true;
      });

      vimeo.on('playing', (playing) => {
        player.classList.remove('-hidden');
        let timeout;

        if (playing && this.startVideoWhenInView && !this.loopVideo) {
          const duration = (playing.duration - 0.2) * 1000;
          timeout = setTimeout(() => {
            vimeo.pause();
          }, duration);
        } else {
          clearTimeout(timeout);
        }
      });

      vimeo.getPaused().then((paused) => {
        if (!paused) {
          if (!isReady) {
            isReady = true;
            this.onReady();
          }
        } else {
          vimeo.off('timeupdate');
        }
        this.paused = paused;
      });
      this.vimeo = vimeo;
    }
  }
}

// Register class
if (window.brix && window.brix.registerBlock) {
  window.brix.registerBlock('brix-video', Video.init);
}
